import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { GetAllSubnetsResponseItem } from "src/api/generated/frontend_internal_v1";
import { Badge } from "src/components/base/badges/base";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { EllipsisVerticalIcon, NoSymbolIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import DeleteSubnetDialog from "src/components/dialogs/delete-subnet";
import DisableSubnetDialog from "src/components/dialogs/disable-subnet";
import { CheckIcon } from "@heroicons/react/24/solid";
import EnableSubnetDialog from "src/components/dialogs/enable-subnet";
import { ApiInternal } from "src/api/api_internal";
import TablePagination from "src/components/table-pagination";
import WS from "src/api/ws";
import UpdateSubnetDialog from "src/components/dialogs/update-subnet";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link ApprovedSubnetsOverview}
 */
export type ApprovedSubnetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all subnets
 */
function ApprovedSubnetsOverview(props: ApprovedSubnetsOverviewProps) {
    const [t] = useTranslation("admin-subnets");
    const [tg] = useTranslation();

    const [openDelete, setOpenDelete] = React.useState<GetAllSubnetsResponseItem>();
    const [openDisable, setOpenDisable] = React.useState<GetAllSubnetsResponseItem>();
    const [openEnable, setOpenEnable] = React.useState<GetAllSubnetsResponseItem>();
    const [editSubnet, setEditSubnet] = React.useState<GetAllSubnetsResponseItem>();

    const { page } = Route.useSearch();
    const router = useRouter();

    const data = Route.useLoaderData();

    useEffect(() => {
        const listener = WS.addEventListener("message.SubnetsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.prefix")}</TableHeader>
                        <TableHeader>{t("label.has-router")}</TableHeader>
                        <TableHeader>{t("label.description")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((subnet) => (
                        <TableRow
                            key={subnet.uuid}
                            href={"/a/subnets/$subnetId/inbound-acls"}
                            params={{ subnetId: subnet.uuid }}
                        >
                            <TableCell>
                                <div className={"flex gap-6"}>
                                    <span>{subnet.name}</span>
                                    {!subnet.has_router && <Badge color={"teal"}>{tg("label.disabled")}</Badge>}
                                </div>
                            </TableCell>
                            <TableCell>
                                {subnet.router ? (
                                    <div className={"flex flex-col gap-1"}>
                                        <span>{subnet.router.dnsname}</span>
                                        <div className={"flex gap-1"}>
                                            {subnet.router.tags.map((tag) => (
                                                <TagBadge key={tag.uuid} tag={tag} />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    "-"
                                )}
                            </TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {subnet.description}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setEditSubnet(subnet)}>
                                                <PencilSquareIcon />
                                                {t("button.edit-description")}
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                                            <DropdownItem
                                                disabled={subnet.has_router}
                                                onClick={() => setOpenEnable(subnet)}
                                            >
                                                <CheckIcon />
                                                <DropdownLabel>{t("button.enable-subnet")}</DropdownLabel>
                                            </DropdownItem>

                                            <DropdownItem
                                                disabled={!subnet.has_router}
                                                onClick={() => setOpenDisable(subnet)}
                                            >
                                                <NoSymbolIcon />
                                                <DropdownLabel>{t("button.disable-subnet")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenDelete(subnet)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-subnet")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/subnets/approved"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />

            {openDelete && (
                <Suspense>
                    <DeleteSubnetDialog
                        subnet={openDelete}
                        onClose={() => setOpenDelete(undefined)}
                        onDeletion={() => setOpenDelete(undefined)}
                    />
                </Suspense>
            )}

            {openDisable && (
                <Suspense>
                    <DisableSubnetDialog
                        subnet={openDisable}
                        onClose={() => setOpenDisable(undefined)}
                        onDisable={() => setOpenDisable(undefined)}
                    />
                </Suspense>
            )}

            {openEnable && (
                <Suspense>
                    <EnableSubnetDialog
                        subnet={openEnable}
                        onClose={() => setOpenEnable(undefined)}
                        onEnable={() => setOpenEnable(undefined)}
                    />
                </Suspense>
            )}

            {editSubnet && (
                <Suspense>
                    <UpdateSubnetDialog
                        subnet={editSubnet}
                        onClose={() => setEditSubnet(undefined)}
                        onSubmit={() => setEditSubnet(undefined)}
                    />
                </Suspense>
            )}
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type ApprovedSubnetsOverviewSearch = {
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/subnets/_subnets/approved")({
    component: ApprovedSubnetsOverview,
    validateSearch: (search: Record<string, unknown>): ApprovedSubnetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page };
    },
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: async ({ deps }) =>
        await ApiInternal.subnets.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
        }),
});
